import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

/**
 * Shared authentication state.
 * @typedef {Object} AuthState
 * @property {'admin'|'user'|null} role - Current role.
 * @property {string} adminToken - Admin token (empty string if not admin).
 * @property {Object|null} selectedUser - Currently selected user profile.
 */

/**
 * Context value exposing auth state and helper functions.
 * @typedef {Object} AuthContextValue
 * @property {AuthState} auth - Current auth state.
 * @property {(role:'admin'|'user', user:Object, token?:string) => void} login - Set role, user, and optional token.
 * @property {() => void} logout - Clear all auth data.
 * @property {(user:Object) => void} setUser - Update selectedUser.
 */

const AuthContext = createContext(/** @type {AuthContextValue|undefined} */ (undefined));

export const AuthProvider = ({ children }) => {
  const [role, setRole] = useState(() => localStorage.getItem('role') || null);
  const [adminToken, setAdminToken] = useState(() => localStorage.getItem('adminToken') || '');
  const [selectedUser, setSelectedUser] = useState(() => {
    const stored = localStorage.getItem('selectedUser');
    try {
      return stored ? JSON.parse(stored) : null;
    } catch (e) {
      return null;
    }
  });

  // Keep localStorage in sync whenever auth values change.
  useEffect(() => {
    if (role) localStorage.setItem('role', role);
    else localStorage.removeItem('role');

    if (adminToken) localStorage.setItem('adminToken', adminToken);
    else localStorage.removeItem('adminToken');

    if (selectedUser) localStorage.setItem('selectedUser', JSON.stringify(selectedUser));
    else localStorage.removeItem('selectedUser');
  }, [role, adminToken, selectedUser]);

  // Sync state across tabs
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'role') setRole(e.newValue);
      if (e.key === 'adminToken') setAdminToken(e.newValue || '');
      if (e.key === 'selectedUser') {
        try {
          setSelectedUser(e.newValue ? JSON.parse(e.newValue) : null);
        } catch (err) {
          setSelectedUser(null);
        }
      }
      if (e.key === null) { // localStorage.clear()
        setRole(null);
        setAdminToken('');
        setSelectedUser(null);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const login = useCallback((newRole, user, token = '') => {
    setRole(newRole);
    setSelectedUser(user);
    if (newRole === 'admin') {
      setAdminToken(token);
    } else {
      setAdminToken('');
    }
  }, []);

  const logout = useCallback(() => {
    setRole(null);
    setAdminToken('');
    setSelectedUser(null);
    localStorage.clear();
  }, []);

  const setUser = useCallback((user) => setSelectedUser(user), []);

  return (
    <AuthContext.Provider value={{ auth: { role, adminToken, selectedUser }, login, logout, setUser }}>
      {children}
    </AuthContext.Provider>
  );
};

/** Hook for consuming the AuthContext inside components. */
export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
};
